// Generated by GitHub Copilot
import React, { useState, useEffect } from "react";
import { Row, Col, Form, Button } from "react-bootstrap";
import { FaSquareMinus } from "react-icons/fa6";
import SimpleMenu from "../SimpleMenu";
import { ComponentAPI } from "../../connections/fetchStatusPageComponents";
import "./StatusPageSelector.css";

/**
 * StatusPageSelector - Reusable component for selecting multiple status page groups and components
 *
 * @param {Object} props - Component props
 * @param {Array} props.targets - Array of selected publishing targets [{group: {label, value}, component: {label, value}}]
 * @param {Function} props.onTargetsChange - Callback when targets array changes
 * @param {Array} props.groupOptions - Available status page groups (optional, will fetch if not provided)
 * @param {boolean} props.showExistingTargetsLabel - Whether to show "Existing Targets" label (default: true)
 * @param {boolean} props.showDivider - Whether to show divider after targets (default: true)
 * @param {number} props.columnSize - Bootstrap column size for group/component selectors (default: 5)
 */
const StatusPageSelector = ({
  targets = [],
  onTargetsChange,
  groupOptions: providedGroupOptions,
  showExistingTargetsLabel = true,
  showDivider = true,
  columnSize = 5,
}) => {
  // Internal state for current selection being added
  const [groupOption, setGroupOption] = useState();
  const [componentOption, setComponentOption] = useState();
  const [componentOptions, setComponentOptions] = useState();
  const [groupOptions, setGroupOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load group options on mount if not provided
  useEffect(() => {
    if (providedGroupOptions && providedGroupOptions.length > 0) {
      setGroupOptions(providedGroupOptions);
    } else {
      setLoading(true);
      ComponentAPI.getGroups()
        .then((groups) => {
          const options = groups.map((g) => ({
            label: g.name,
            value: g.id,
          }));
          setGroupOptions(options);
        })
        .catch((error) => {
          console.error("Error fetching status page groups:", error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [providedGroupOptions]);

  // Load components when group changes
  useEffect(() => {
    if (groupOption && groupOption.value) {
      ComponentAPI.getComponents(groupOption.value)
        .then((components) => {
          const options = components.map((c) => ({
            label: c.name,
            value: c.id,
          }));
          setComponentOptions(options);
        })
        .catch((error) => {
          console.error("Error fetching components:", error);
        });
    }
  }, [groupOption]);

  // Add a new publishing target
  const handleAddTarget = (componentSelection) => {
    if (!groupOption || !componentSelection) {
      return;
    }

    const newTarget = {
      group: groupOption,
      component: componentSelection,
    };

    const newTargets = [...targets, newTarget];
    onTargetsChange(newTargets);

    // Reset selection state
    setGroupOption({ label: "", value: "" });
    setComponentOption({ label: "", value: "" });
    setComponentOptions(undefined);
  };

  // Remove a publishing target
  const handleRemoveTarget = (groupValue, componentValue) => {
    const filteredTargets = targets.filter((target) => {
      return (
        target.group.value !== groupValue ||
        target.component.value !== componentValue
      );
    });
    onTargetsChange(filteredTargets);
  };

  // Handle group selection change
  const handleGroupChange = (option) => {
    setGroupOption(option);
    setComponentOptions(undefined);
    setComponentOption(undefined);
  };

  // Handle component selection change
  const handleComponentChange = (option) => {
    setComponentOption(option);
    handleAddTarget(option);
  };

  return (
    <div className="status-page-selector">
      <Row>
        <Col xs={columnSize} md={columnSize} lg={columnSize} xl={columnSize}>
          <Form.Label>Status Page Group</Form.Label>
        </Col>
        <Col xs={columnSize} md={columnSize} lg={columnSize} xl={columnSize}>
          <Form.Label>Status Page Component</Form.Label>
        </Col>
      </Row>
      <Row>
        <Col xs={columnSize} md={columnSize} lg={columnSize} xl={columnSize}>
          {/* This SimpleMenu is the shared components/SimpleMenu/index.js, which has
              no testId prop (unlike ProblemForm's local one) — wrap externally instead. */}
          <div data-testid="statuspage-group-select">
            <SimpleMenu
              defaultValue={groupOption}
              options={groupOptions}
              loading={loading}
              onChange={handleGroupChange}
            />
          </div>
        </Col>
        {componentOptions ? (
          <Col xs={columnSize} md={columnSize} lg={columnSize} xl={columnSize}>
            <div data-testid="statuspage-component-select">
              <SimpleMenu
                defaultValue={componentOption}
                options={componentOptions}
                loading={false}
                onChange={handleComponentChange}
              />
            </div>
          </Col>
        ) : (
          <Col>
            <p>Select a Group First</p>
          </Col>
        )}
      </Row>

      {showExistingTargetsLabel && (
        <Row>
          <Col>
            <Form.Label>Existing Targets</Form.Label>
          </Col>
        </Row>
      )}

      {targets.length === 0 && <p>No Targets Set</p>}
      {targets.length > 0 &&
        targets.map((target, index) => (
          <Row
            key={`${target.group.value}-${target.component.value}-${index}`}
            className="target-row"
          >
            <Col>{target.group.label}</Col>
            <Col>{target.component.label}</Col>
            <Col>
              <Button
                variant="danger"
                className="d-flex align-items-center gap-1"
                onClick={() =>
                  handleRemoveTarget(target.group.value, target.component.value)
                }
              >
                <FaSquareMinus /> Remove
              </Button>
            </Col>
          </Row>
        ))}

      {showDivider && (
        <Row>
          <Col>
            <hr />
          </Col>
        </Row>
      )}
    </div>
  );
};

export default StatusPageSelector;
